import {
	ChangeDetectionStrategy,
	Component,
	computed,
	inject,
	input,
	InputSignal,
	output,
	OutputEmitterRef,
	signal,
	Signal,
	WritableSignal
} from "@angular/core";
import { ReferenceDataState, selectReferenceDataStatus, selectCurrencies } from "@core/reference-data";
import { Currency, sortCurrencies } from "@domains/currency";
import { addDurationToUtcNow } from "@domains/date";
import { BlotterForm, BlotterFormComponent } from "@features/blotter";
import { Store } from "@ngrx/store";
import { isLoading, StoreStatus } from "@utility/state";
import { TradeCostAnalysisSummaryResponse } from "../../../../models/api/responses/summary/trade-cost-analysis-summary.response.model";
import { TradeCostAnalysisFormType } from "../../../../models/forms/trade-cost-analysis-form.type";
import { TradeCostAnalysisService } from "../../../../services/trade-cost-analysis/trade-cost-analysis.service";

@Component({
	selector: "lib-trade-cost-analysis-widget",
	imports: [BlotterFormComponent],
	templateUrl: "./trade-cost-analysis-widget.component.html",
	styleUrl: "./trade-cost-analysis-widget.component.scss",
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class TradeCostAnalysisWidgetComponent {
	// injectors
	protected readonly referenceDataStore = inject(Store<ReferenceDataState>);
	private readonly tradeCostAnalysisService = inject(TradeCostAnalysisService);

	// inputs
	public readonly formType$: InputSignal<TradeCostAnalysisFormType> = input("live" as TradeCostAnalysisFormType);

	// outputs
	public tradeCostAnalysisReceived: OutputEmitterRef<TradeCostAnalysisSummaryResponse> = output();

	// store values
	private readonly currencies$ = this.referenceDataStore.selectSignal(selectCurrencies);

	// static values
	protected readonly defaultTimeZone = "Europe/London";
	protected readonly defaultDealtCurrencyCode = "USD";
	protected readonly defaultContraCurrencyCode = "GBP";
	protected readonly defaultBaseIsSell = true;
	protected readonly defaultBaseIsDealt = true;
	protected readonly tradeDateMinDate = addDurationToUtcNow({ months: -6 });
	protected readonly valueDateMonthOffsetFromTradeDate = 24;

	// declarations
	protected readonly interbankForwardRate$: WritableSignal<number | undefined> = signal(undefined);

	protected readonly currenciesWithLiveRatesEnabled$: Signal<Currency[]> = computed(() => {
		const currencies = this.currencies$().filter((currency) => currency.settings.hasLiveRates === true);
		return sortCurrencies([...currencies]);
	});

	protected readonly configurationStatus$: Signal<StoreStatus> =
		this.referenceDataStore.selectSignal(selectReferenceDataStatus);

	protected readonly isLoading$: Signal<boolean> = computed(() => {
		return isLoading(this.configurationStatus$());
	});
	protected readonly isLiveAnalysis$: Signal<boolean> = computed(() => {
		return this.formType$() === "live";
	});

	protected readonly isApiLoading$: WritableSignal<boolean> = signal(false);
	protected readonly showErrorMessage$: WritableSignal<boolean> = signal(false);

	// functions
	protected submitForm(forward: BlotterForm): void {
		this.isApiLoading$.set(true);
		if (this.formType$() === "live") {
			this.tradeCostAnalysisService.postLiveForward(forward).subscribe({
				next: (response: TradeCostAnalysisSummaryResponse) => {
					this.handleTradeCostAnalysisResponse(response);
				},
				error: (err) => {
					this.isApiLoading$.set(false);
					this.showErrorMessage$.set(true);
				}
			});
		} else {
			this.tradeCostAnalysisService.postDemoForward(forward).subscribe({
				next: (response: TradeCostAnalysisSummaryResponse) => {
					this.handleTradeCostAnalysisResponse(response);
				},
				error: (err) => {
					this.isApiLoading$.set(false);
					this.showErrorMessage$.set(true);
				}
			});
		}
	}

	private handleTradeCostAnalysisResponse(tradeCostAnalysisSummary: TradeCostAnalysisSummaryResponse): void {
		const isRateValid = this.isRateValid(tradeCostAnalysisSummary);

		if (isRateValid === true) {
			this.tradeCostAnalysisReceived.emit(tradeCostAnalysisSummary);
		} else {
			this.interbankForwardRate$.set(tradeCostAnalysisSummary.costOfHedging.rates.interbankForwardRate);
		}

		this.isApiLoading$.set(false);
		this.showErrorMessage$.set(false);
	}

	private isRateValid(tradeCostAnalysisSummary: TradeCostAnalysisSummaryResponse): boolean {
		const forwardRate = tradeCostAnalysisSummary.costOfHedging.rates.interbankForwardRate;
		const formRate = tradeCostAnalysisSummary.forwardDetails.rate;
		const baseIsSell = tradeCostAnalysisSummary.forwardDetails.baseIsSell;

		if ((baseIsSell === true && formRate > forwardRate) || (baseIsSell === false && formRate < forwardRate)) {
			return false;
		}
		return true;
	}
}
